import React from "react";
import { unstable_batchedUpdates as batch } from "react-dom";
import { GroupedErrors, PropError } from "../PropX";
import { useEvent } from "./useEvent";

type Props<InputElement extends HTMLInputElement | HTMLTextAreaElement, FocusElement extends HTMLElement> = {
  error?: PropError | PropError[];
  disabled?: boolean;
  value?: string | number;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  required?: boolean;
  onChange?: React.ChangeEventHandler<InputElement>;
  onBlur?: React.FocusEventHandler<FocusElement>;
  onFocus?: React.FocusEventHandler<FocusElement>;
};

type Config<InputElement extends HTMLInputElement | HTMLTextAreaElement> = {
  ref: React.RefObject<InputElement>;
  validateOn?: "change" | "blur" | "manual";
};

type InputProps<InputElement extends HTMLInputElement | HTMLTextAreaElement, FocusElement extends HTMLElement> = {
  invalid: boolean;
  onInvalid: React.FormEventHandler<InputElement>;
  onChange: React.ChangeEventHandler<InputElement>;
  onBlur: React.FocusEventHandler<FocusElement>;
  onFocus: React.FocusEventHandler<FocusElement>;
};

type Force = {
  checkValidity(): boolean;
  displayErrorMessage(next: string): void;
};

type Result<InputElement extends HTMLInputElement | HTMLTextAreaElement, FocusElement extends HTMLElement> = [
  errors: GroupedErrors,
  inputProps: InputProps<InputElement, FocusElement>,
  force: Force,
  focused: boolean
];

/**
 * This hook is a wrapper around browser validation logic so that we can consistently apply validation
 * rules to HTMLInputElement components regardless of what type of component they are.
 *
 * @private
 */
export const useHTMLInputElementValidationProjection = <
  InputElement extends HTMLInputElement | HTMLTextAreaElement,
  FocusElement extends HTMLElement
>(
  props: Props<InputElement, FocusElement>,
  config: Config<InputElement>
): Result<InputElement, FocusElement> => {
  const { disabled = false } = props;
  const { ref, validateOn = "blur" } = config;
  const [focused, setFocused] = React.useState(false);
  const [inputErrorMessage, setInputErrorMessage] = React.useState<string | null>(null);

  const errors = React.useMemo(
    () => PropError.groupAll(props.error, inputErrorMessage, disabled),
    [props.error, inputErrorMessage, disabled]
  );

  const checkValidity = useEvent(() => {
    const valid = ref.current?.checkValidity();

    if (valid) {
      setInputErrorMessage(null);
    }

    return valid ?? true;
  });

  /**
   * We keep track of whether the input has been touched to avoid validating before the user
   * has interacted with it. We have an additional check that revalidates the input if the
   * props such as `maxLength` or `required` change, but we don't want those to trigger a validation
   * if the input has never been touched.
   */
  const [touched, setTouched] = React.useState(false);

  /**
   * We want to make sure that we re-validate when the value
   * or any of the validation-specific attributes have change.
   */
  React.useLayoutEffect(() => {
    if (validateOn === "manual") return;

    if (!ref.current) return;

    /**
     * If the input has not been touched, then we usually don't want to validate it.
     */
    if (!touched) return;
    /**
     * Never automatically check the validation when the input is focused because it's just a better UX.
     */
    if (focused) return;

    checkValidity();
  }, [
    validateOn,
    ref.current,
    focused,
    props.min,
    props.max,
    props.minLength,
    props.maxLength,
    props.pattern,
    props.required,
    props.disabled,
  ]);

  const onChange = useEvent<React.ChangeEventHandler<InputElement>>((ev) => {
    batch(() => {
      if (!touched) setTouched(true);
      setInputErrorMessage(null);
      props.onChange?.(ev);
      if (validateOn === "change") checkValidity();
    });
  });

  const onBlur = useEvent<React.FocusEventHandler<FocusElement>>((ev) => {
    batch(() => {
      if (!touched) setTouched(true);
      /**
       * Triggering the validation on blur is implicit in `focused` being set to `false`.
       */
      setFocused(false);
      props.onBlur?.(ev);
      /**
       * The following is commented out and left here intentionally to show that we DO NOT
       * need to do this here because the validation is already triggered by `useLayoutEffect`
       * above that listens for changes to `touched` and `focused`.
       */
      // if (validateOn === "blur") checkValidity();
    });
  });

  const onFocus = useEvent<React.FocusEventHandler<FocusElement>>((ev) => {
    batch(() => {
      if (!touched) setTouched(true);
      setFocused(true);
      props.onFocus?.(ev);
    });
  });

  const onInvalid = useEvent<React.FormEventHandler<InputElement>>((ev) => {
    ev.preventDefault();
    const message = PropError.getValidationMessageForInput(ev.currentTarget, errors);
    setInputErrorMessage(message);
  });

  const invalid = !disabled && errors.some;

  const inputProps = React.useMemo(() => {
    return {
      invalid,
      onInvalid,
      onChange,
      onBlur,
      onFocus,
    };
  }, [invalid]);

  const force = React.useMemo(() => {
    return {
      checkValidity,
      displayErrorMessage: setInputErrorMessage,
    };
  }, [checkValidity, setInputErrorMessage]);

  return [errors, inputProps, force, focused];
};
